import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { MainContent, PageHeading } from '../../../components/Shell/Content';
import { classNames } from '../../../components/helpers/functions';
import LoadingSkeleton from '../../../components/Shell/LoadingSkeleton';

import useAsync from '../../../hooks/useAsync';
import { getModule } from '../../../api/modules';

import Categories from './Categories';
import Fields from './Fields';
import AvailableFields from './AvailableFields';
import Tags from './Tags';
import AssetSettings from './AssetSettings';

const TypesTab = () => {
	return <div>Types</div>;
};

const FieldsTab = () => {
	return <div>Fields</div>;
};

const Modules = () => {
	const navigate = useNavigate();
	const { id, tab } = useParams();

	const {
		value: module,
		status: loadingModule,
		execute: onRefreshModule,
	} = useAsync(getModule, {
		defaultPayload: { id },
	});

	const AVAILABLE_TABS = {
		fields: {
			name: 'Data Structure',
			key: 'fields',
			current: !tab || tab === 'fields',
			onClick: () => navigate(`/settings/data-structures/${id}/fields`),
			component: () => <Fields module={module} onRefresh={onRefreshModule} />,
		},
		categories: {
			name: 'Categories',
			key: 'categories',
			current: tab === 'categories',
			onClick: () => navigate(`/settings/data-structures/${id}/categories`),
			component: () => (
				<Categories module={module} onRefresh={onRefreshModule} />
			),
		},
		tags: {
			name: 'Tags',
			key: 'tags',
			current: tab === 'tags',
			onClick: () => navigate(`/settings/data-structures/${id}/tags`),
			component: () => <Tags module={module} onRefresh={onRefreshModule} />,
		},
		['asset-settings']: {
			name: 'Settings',
			key: 'asset-settings',
			current: tab === 'asset-settings',
			onClick: () =>
				navigate(`/settings/data-structures/${id}/asset-settings`),
			component: () => (
				<AssetSettings module={module} onRefresh={onRefreshModule} />
			),
		},
	};

	const MODULE_CONFIG = {
		'default:asset': {
			tabs: ['fields', 'categories', 'tags', 'asset-settings'],
		},
		'default:consumable': {
			tabs: ['fields', 'categories'],
		},
		'default:storage-point': {
			tabs: ['fields', 'categories'],
		},
		'default:document': {
			tabs: ['fields', 'categories'],
		},
		'default:service': {
			tabs: ['fields'],
		},
		'default:event': {
			tabs: ['fields', 'categories'],
		},
		'default:contract': {
			tabs: ['fields', 'categories'],
		},
	}[module?.key];

	const TABS = MODULE_CONFIG?.tabs?.map((key) => AVAILABLE_TABS[key]) || [];

	const currentTab = AVAILABLE_TABS[tab];

	return (
		<MainContent
			maxWidth="w-full"
			sidebarRight={
				currentTab?.key === 'fields' ||
				currentTab?.key === 'categories' ||
				!tab ? (
					<div>
						<AvailableFields module={module} onRefresh={onRefreshModule} />
					</div>
				) : null
			}
		>
			{loadingModule === 'pending' ? (
				<LoadingSkeleton />
			) : (
				<>
					<PageHeading onBack={() => navigate('/settings')}>
						{`Manage Data Structure: ${module?.name}`}
					</PageHeading>

					<div className="border-b border-gray-200">
						<div className="sm:flex sm:items-baseline">
							<div className="mt-4 sm:mt-0">
								<nav className="-mb-px flex space-x-8">
									{TABS.map((tab) => {
										return (
											<a
												key={tab.name}
												onClick={() =>
													typeof tab.onClick === 'function'
														? tab.onClick()
														: null
												}
												className={classNames(
													tab.current
														? 'border-indigo-500 text-indigo-600'
														: 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
													'whitespace-nowrap border-b-2 px-1 pb-4 text-sm font-medium cursor-pointer'
												)}
												aria-current={tab.current ? 'page' : undefined}
											>
												{tab.name}
											</a>
										);
									})}
								</nav>
							</div>
						</div>
					</div>

					<div className="my-4">
						{AVAILABLE_TABS[tab]?.component() ||
							AVAILABLE_TABS?.fields?.component()}
					</div>
				</>
			)}
		</MainContent>
	);
};

export default Modules;
